import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Menu } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useScrolled } from '@/hooks/use-scrolled'
import { Logo } from '@/components/generals/logo'
import { MobileMenu } from '@/components/generals/mobile-menu'
import { PillButton } from '@/components/generals/pill-button'
import { useAuth } from '@/components/generals/auth-provider'
import { PRIMARY_NAV } from '@/constants/navigation'

/**
 * Floating, centered glass bar. It blurs what is behind it and gently
 * narrows once the visitor starts scrolling.
 */
export const Navbar = () => {
    const router = useRouter()
    const scrolled = useScrolled(40)
    const { member } = useAuth()
    const [menuOpen, setMenuOpen] = useState(false)
    const menuButtonRef = useRef<HTMLButtonElement>(null)

    const closeMenu = useCallback(() => {
        setMenuOpen(false)
        menuButtonRef.current?.focus()
    }, [])

    useEffect(() => {
        const close = () => setMenuOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    const isActive = (href: string) => router.pathname === href || router.pathname.startsWith(`${href}/`)

    return (
        <>
            <header className='pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center px-3 pt-3 sm:px-5 sm:pt-4'>
                <nav
                    aria-label='Main'
                    className={cn(
                        'glass pointer-events-auto grid w-full grid-cols-[1fr_auto] items-center rounded-full pl-5 pr-2 transition-[max-width,height,background-color] duration-700 ease-cinema lg:grid-cols-[1fr_auto_1fr]',
                        'motion-rise [--rise-delay:200ms]',
                        scrolled ? 'h-[54px] max-w-[940px] bg-black/45' : 'h-[62px] max-w-[1180px] bg-black/10'
                    )}
                >
                    <div className='flex items-center'>
                        <Logo />
                    </div>

                    <ul className='hidden items-center gap-0.5 lg:flex'>
                        {PRIMARY_NAV.map(item => {
                            const active = isActive(item.href)

                            return (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        aria-current={active ? 'page' : undefined}
                                        className={cn(
                                            'relative inline-flex rounded-full px-4 py-2 text-[13.5px] tracking-[-0.01em] transition-colors duration-300 hover:bg-white/[0.06] hover:text-cream',
                                            active ? 'text-cream' : 'text-cream/65'
                                        )}
                                    >
                                        {item.label}

                                        {active && (
                                            <span aria-hidden='true' className='absolute bottom-[3px] left-1/2 size-1 -translate-x-1/2 rounded-full bg-citron' />
                                        )}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>

                    <div className='flex items-center justify-end gap-1.5'>
                        <Link
                            href={member ? '/account' : '/login'}
                            className='hidden rounded-full px-3.5 py-2 text-[13.5px] tracking-[-0.01em] text-cream/65 transition-colors duration-300 hover:text-cream lg:inline-flex'
                        >
                            {member ? 'Account' : 'Login'}
                        </Link>

                        <PillButton href='/find-an-advisor' size='sm' className='hidden lg:inline-flex'>
                            Find an advisor
                        </PillButton>

                        <button
                            ref={menuButtonRef}
                            type='button'
                            onClick={() => setMenuOpen(true)}
                            aria-label='Open menu'
                            aria-expanded={menuOpen}
                            aria-controls='mobile-menu'
                            className='grid size-11 place-items-center rounded-full text-cream transition-colors hover:bg-white/10 lg:hidden'
                        >
                            <Menu className='size-5' />
                        </button>
                    </div>
                </nav>
            </header>

            <MobileMenu open={menuOpen} onClose={closeMenu} />
        </>
    )
}
